import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { channelLabel } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAutomationTemplate, useUseAutomationTemplate } from '../../api/automation-templates';
import { useChannels } from '../../api/channels';
import { usePipelines } from '../../api/pipelines';
import { useAutomationFlows } from '../../api/automation-flows';
import {
  blankForm,
  describeAutomation,
  formFromDefinition,
  knownReplyOptions,
} from '../automations/automation-model';
import { AutomationDiagram } from '../automations/AutomationDefinitionEditor';

const TIME_BASED_TRIGGERS = new Set(['FOLLOW_UP_DUE', 'TASK_DUE']);

/** Copies a template into a new, editable DRAFT automation (docs/API_SPEC.md §14). */
export function AutomationTemplateUsePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const template = useAutomationTemplate(id);
  const use = useUseAutomationTemplate(id ?? '');
  // WhatsApp numbers and website chats.
  const channels = useChannels();
  const pipelines = usePipelines();
  const flows = useAutomationFlows();

  /** The template as the When / If / Then diagram + a plain-English sentence. */
  const preview = useMemo(() => {
    const def = template.data?.definition;
    if (!def) return null;
    const form = formFromDefinition(blankForm(), def);
    const actions = def.actions.map((a) => ({
      ...a,
      config: a.config as unknown as Record<string, unknown>,
    }));
    const buttonTitles = Object.fromEntries(
      knownReplyOptions([{ name: '', actions }]).map((o) => [o.id, o.title]),
    );
    return {
      form,
      sentence: describeAutomation(
        {
          triggerTypes: def.triggers.map((t) => t.triggerType),
          conditions: def.conditions.map((c) => ({ ...c, value: c.value ?? null })),
          actions,
        },
        {
          flowName: (k) => flows.data?.items.find((f) => f.key === k)?.name,
          buttonTitles,
        },
      ),
    };
  }, [template.data, flows.data]);

  const [name, setName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (template.data) setName((n) => n || template.data.name);
  }, [template.data]);

  if (!id) return <PageState kind="error" title="Template not found" />;
  if (template.isPending) return <PageState kind="loading" message="Loading template…" />;
  if (template.isError)
    return (
      <PageState
        kind="error"
        message={template.error.message}
        onRetry={() => void template.refetch()}
      />
    );

  const t = template.data;
  const isTimeBased = t.definition.triggers.some((trig) =>
    TIME_BASED_TRIGGERS.has(trig.triggerType),
  );
  const hasCreateLead = t.definition.actions.some((a) => a.actionType === 'CREATE_LEAD');
  const stages = pipelines.data?.find((p) => p.id === pipelineId)?.stages ?? [];

  function clearFieldError(key: string) {
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const trimmed = name.trim();
    if (trimmed.length < 1) errors.name = 'Enter a name.';
    else if (trimmed.length > 255) errors.name = 'Name must be 255 characters or fewer.';
    if (hasCreateLead) {
      if (!pipelineId) errors.pipelineId = 'Select a pipeline — this template creates a lead.';
      if (!stageId) errors.stageId = 'Select a stage.';
    }
    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    try {
      const created = await use.mutateAsync({
        name: name.trim(),
        channelId: isTimeBased ? null : channelId || null,
        ...(hasCreateLead ? { pipelineId, stageId } : {}),
      });
      toast.success('Copied as a draft — review and edit its replies, then activate it.');
      navigate(`/automations/${created.id}/edit`, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        // Server field keys are the automation's own shape (e.g. actions/1/config/pipelineId) -
        // map anything pipeline/stage-related onto this form's own fields.
        const mapped: Record<string, string> = {};
        for (const [key, message] of Object.entries(serverFieldErrors)) {
          if (key.toLowerCase().includes('pipelineid')) mapped.pipelineId = message;
          else if (key.toLowerCase().includes('stageid')) mapped.stageId = message;
          else if (key === 'name') mapped.name = message;
        }
        if (Object.keys(mapped).length > 0) {
          setFieldErrors(mapped);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not create the automation from this template.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Use "{t.name}"</h1>

      <Card title="What this template does">
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {t.description ? <p style={{ margin: 0 }}>{t.description}</p> : null}
          {preview ? (
            <>
              <p role="status" aria-label="Summary" style={{ margin: 0 }}>
                {preview.sentence}
              </p>
              {preview.form ? (
                <AutomationDiagram form={preview.form} flows={flows.data?.items ?? []} />
              ) : null}
            </>
          ) : null}
        </div>
      </Card>

      <Card>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Name for your copy"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            error={fieldErrors.name}
          />

          {isTimeBased ? (
            <p
              style={{
                margin: 0,
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              This template's trigger is time-based, so it applies to all channels — there's no
              channel to choose.
            </p>
          ) : (
            <Select
              label="On which channel?"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              options={[
                { value: '', label: 'All my WhatsApp numbers' },
                ...(channels.data ?? []).map((c) => ({ value: c.id, label: channelLabel(c) })),
              ]}
            />
          )}

          {hasCreateLead ? (
            <>
              <Select
                label="Pipeline"
                value={pipelineId}
                onChange={(e) => {
                  setPipelineId(e.target.value);
                  setStageId('');
                  clearFieldError('pipelineId');
                }}
                options={[
                  { value: '', label: 'Select a pipeline' },
                  ...(pipelines.data ?? []).map((p) => ({ value: p.id, label: p.name })),
                ]}
                error={fieldErrors.pipelineId}
              />
              <Select
                label="Stage"
                value={stageId}
                onChange={(e) => {
                  setStageId(e.target.value);
                  clearFieldError('stageId');
                }}
                disabled={!pipelineId}
                options={[
                  { value: '', label: 'Select a stage' },
                  ...stages.map((s) => ({ value: s.id, label: s.name })),
                ]}
                error={fieldErrors.stageId}
              />
            </>
          ) : null}

          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button type="submit" disabled={use.isPending}>
              {use.isPending ? 'Creating…' : 'Create draft automation'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/automation-templates')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/automation-templates">← All templates</Link>
      </p>
    </div>
  );
}
